import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Compliance Detail View — GLÖZ checks, N-Bilanz overview, inspector token management.
 * Accessible from Dashboard kachel or Hamburger menu.
 */
export default function ComplianceDetail({ token, onClose }) {
  const { t } = useTranslation();
  const [gloez, setGloez] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gRes, tRes] = await Promise.allSettled([
        fetch(`${API}/api/v1/aif/compliance/gloez`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/v1/aif/compliance/inspector-tokens`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (gRes.status === 'fulfilled' && gRes.value.ok) setGloez(await gRes.value.json());
      if (tRes.status === 'fulfilled' && tRes.value.ok) { const d = await tRes.value.json(); setTokens(d.tokens || []); }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const createToken = async () => {
    setCreating(true);
    const now = new Date();
    const yearStart = `${now.getFullYear()}-01-01`;
    const today = now.toISOString().split('T')[0];
    try {
      await apiClient.post('/api/v1/aif/compliance/inspector-token', { inspection_type: 'GENERAL', data_from: yearStart, data_to: today, expires_hours: 24 }, { queueOnOffline: true });
      fetchData();
    } catch { /* ApiError */ }
    setCreating(false);
  };

  const revokeToken = async (id) => {
    try {
      await apiClient.delete(`/api/v1/aif/compliance/inspector-tokens/${id}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      fetchData();
    } catch { /* ApiError */ }
  };

  const STATUS_BADGE = { konform: 'bg-green-100 text-green-700', warnung: 'bg-amber-100 text-amber-700', verletzung: 'bg-red-100 text-red-700', keine_daten: 'bg-gray-100 text-[var(--aif-text-muted)]' };
  const STATUS_ICON = { konform: '\u2705', warnung: '\u26A0\uFE0F', verletzung: '\u274C', keine_daten: '\u2753' };

  return (
    <div className="space-y-6 px-1">
      {/* GLÖZ Status */}
      <div>
        <h3 className="text-base font-semibold text-[var(--aif-text-primary)] mb-3">{t('compliance.gloezTitle')}</h3>
        {loading ? (
          <div className="flex justify-center py-6"><div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" /></div>
        ) : gloez ? (
          <div className="space-y-2">
            <div className="text-xs text-[var(--aif-text-muted)] mb-2">
              {gloez.summary?.konform}/{gloez.summary?.total} {t('compliance.konform')}
            </div>
            {(gloez.checks || []).map((c, i) => (
              <div key={i} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-3">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-[var(--aif-text-primary)]">{c.standard}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[c.status] || ''}`}>
                    {STATUS_ICON[c.status]} {c.status}
                  </span>
                </div>
                <p className="text-xs text-[var(--aif-text-muted)]">{c.name}: {c.detail}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--aif-text-muted)]">{t('compliance.noData')}</p>
        )}
      </div>

      {/* Inspector Token Management */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[var(--aif-text-primary)]">{t('compliance.inspectorAccess')}</h3>
          <button onClick={createToken} disabled={creating} className="px-3 py-1.5 rounded-lg bg-[var(--aif-color-primary)] text-white text-xs font-medium disabled:opacity-50">
            {creating ? '...' : t('compliance.createToken')}
          </button>
        </div>

        {tokens.length === 0 ? (
          <p className="text-sm text-[var(--aif-text-muted)]">{t('compliance.noTokens')}</p>
        ) : (
          <div className="space-y-2">
            {tokens.map((tk, i) => (
              <div key={i} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-[var(--aif-text-primary)]">{tk.inspection_type}</span>
                  {tk.is_active ? (
                    <button onClick={() => revokeToken(tk.id)} className="text-[10px] text-[var(--aif-color-danger)]">{t('share.revoke')}</button>
                  ) : (
                    <span className="text-[10px] text-[var(--aif-text-muted)]">{tk.revoked_at ? t('share.revoked') : t('share.expired')}</span>
                  )}
                </div>
                <div className="text-[10px] text-[var(--aif-text-muted)]">
                  {tk.data_from} — {tk.data_to} &middot; {t('share.views')}: {tk.access_count}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
